import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, FreeMode, Mousewheel, Zoom } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import { Helmet } from 'react-helmet';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
import { INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Header from '../components/Header.js';

import './slider-freemode.scss'
import './CommissionsSlider.scss'
import { getFullScreenUrl, getSliderUrl, isVideo, getVideoUrl } from '../utils/imageLoader.js';

function Portraits02Slider({ commissions, loading = true, buttons = null }) {
    const { slug } = useParams();
    const commission = commissions.find(comm => comm.slug === slug);
    const [imageDimensions, setImageDimensions] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeImage, setActiveImage] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(1);
    const [imagesLoaded, setImagesLoaded] = useState({});
    const [allImagesReady, setAllImagesReady] = useState(false);
    const [modalSwiper, setModalSwiper] = useState(null);
    const [modalReady, setModalReady] = useState(false);
    const [modalImagesLoaded, setModalImagesLoaded] = useState({});

    // Scroll to top on mount
    useEffect(() => {
        const prev = typeof window !== 'undefined' && window.history && 'scrollRestoration' in window.history ? window.history.scrollRestoration : null;
        if (prev !== null) window.history.scrollRestoration = 'manual';
        const scrollTop = () => {
            if (lenisRef.current && typeof lenisRef.current.scrollTo === 'function') {
                lenisRef.current.scrollTo(0, { immediate: true });
            } else {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
            }
        };
        if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(() => {
                scrollTop();
                setTimeout(scrollTop, 0);
                setTimeout(scrollTop, 100);
            });
        } else {
            setTimeout(scrollTop, 0);
            setTimeout(scrollTop, 100);
        }
        return () => {
            if (prev !== null) window.history.scrollRestoration = prev;
        };
    }, [slug]);

    // Smooth scroll with Lenis
    const lenisRef = useRef(null);
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        lenisRef.current = lenis;

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    const measureActiveModalImage = () => {
        const active = document.querySelector('.modal-swiper .swiper-slide-active img[data-index]');
        if (!active) return;
        const idx = active.getAttribute('data-index');
        const rect = active.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setImageDimensions(prev => ({
                ...prev,
                [`modal-${idx}`]: { width: Math.round(rect.width), height: Math.round(rect.height) }
            }));
        }
    };

    // Re-measure active modal image on resize when modal is open
    useEffect(() => {
        if (!isModalOpen) return;
        const onResize = () => measureActiveModalImage();
        measureActiveModalImage();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isModalOpen, activeIndex]);



    // Enable trackpad pinch-to-zoom on desktop (ctrl+wheel) for modal swiper
    /*     useEffect(() => {
            if (!modalSwiper || !modalSwiper.el) return;
            const el = modalSwiper.el;
            const onWheel = (e) => {
                // On macOS trackpads, pinch generates wheel events with ctrlKey=true in Chromium/Firefox
                if (!e.ctrlKey) return;
                if (typeof e.preventDefault === 'function') e.preventDefault();
                if (e.deltaY < 0) {
                    modalSwiper.zoom && typeof modalSwiper.zoom.in === 'function' && modalSwiper.zoom.in(e);
                } else {
                    modalSwiper.zoom && typeof modalSwiper.zoom.out === 'function' && modalSwiper.zoom.out(e);
                }
            };
            el.addEventListener('wheel', onWheel, { passive: false });
            return () => {
                el.removeEventListener('wheel', onWheel);
            };
        }, [modalSwiper]); */




    // When modal opens or activeIndex changes, measure after layout and after transition
    useEffect(() => {
        if (!isModalOpen) return;
        const rafMeasure = () => measureActiveModalImage();
        if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(rafMeasure);
            setTimeout(measureActiveModalImage, 350);
        } else {
            setTimeout(measureActiveModalImage, 0);
            setTimeout(measureActiveModalImage, 350);
        }
    }, [isModalOpen, activeIndex]);

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.overscrollBehaviorX = 'contain';
            document.body.style.touchAction = 'pan-y pinch-zoom';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        }

        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        };
    }, [isModalOpen]);

    // Add effect to handle escape key
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isModalOpen) {
                setIsModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [isModalOpen]);

    // Navigate to correct slide when modal opens
    useEffect(() => {
        if (isModalOpen && modalSwiper) {
            setModalReady(false);
            requestAnimationFrame(() => {
                modalSwiper.slideToLoop(activeIndex, 0);
                setTimeout(() => {
                    setModalReady(true);
                }, 50);
            });
        } else {
            setModalReady(false);
        }
    }, [isModalOpen, modalSwiper, activeIndex]);

    // Only use slider pictures (mainPicture is just for preview/thumbnail)
    const allImages = useMemo(() => {
        return commission ? (commission.sliderPictures || []).filter(Boolean) : [];
    }, [commission]);

    // Disable modal navigation if any slide is a video
    const hasAnyVideo = useMemo(() => allImages.some(img => isVideo(img)), [allImages]);

    // Check if all images are loaded
    useEffect(() => {
        const totalImages = allImages.length;
        const loadedCount = Object.keys(imagesLoaded).filter(key => imagesLoaded[key]).length;

        if (totalImages > 0 && loadedCount === totalImages) {
            setAllImagesReady(true);
        }
    }, [imagesLoaded, allImages.length]);

    // Reset loading state when commission changes
    useEffect(() => {
        setImagesLoaded({});
        setAllImagesReady(false);
    }, [slug]);


    // Only show error messages after loading completes
    if (!loading) {
        if (!commission) {
            return <div>Project not found</div>;
        }

        // Check if commission has required fields
        if (!commission.mainPicture && (!commission.sliderPictures || commission.sliderPictures.length === 0)) {
            return <div>No images available for this commission</div>;
        }
    }

    // Return null while loading to prevent flash
    if (loading || !commission) {
        return null;
    }

    const getImageUrl = (image) => {
        return getSliderUrl(image);
    };

    const handleImageLoad = (event, index) => {
        const img = event.target;
        setImageDimensions(prev => ({
            ...prev,
            [index]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }));

        setImagesLoaded(prev => {
            const updated = { ...prev, [index]: true };
            return updated;
        });
    };

    const handleModalImageLoad = (event, index) => {
        const imgEl = event.target;
        const measure = () => {
            const slide = imgEl.closest('.swiper-slide');
            if (!slide || !slide.classList.contains('swiper-slide-active')) return;
            const rect = imgEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                setImageDimensions(prev => ({
                    ...prev,
                    [`modal-${index}`]: {
                        width: Math.round(rect.width),
                        height: Math.round(rect.height)
                    }
                }));
            }
        };
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
        setModalImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
        if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(measure);
        } else {
            setTimeout(measure, 0);
        }
    };

    const handleVideoLoad = (index) => {
        setImagesLoaded(prev => {
            const updated = { ...prev, [index]: true };
            return updated;
        });
    };

    const handleModalVideoLoad = (index) => {
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
        setModalImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleImageClick = (index) => {
        console.log('Clicked image:', allImages[index]);
        setActiveIndex(index);
        setActiveImage(allImages[index]);
        setCurrentSlide(index + 1);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const renderOptions = {
        renderText: text =>
            text.split('\n').reduce((children, textSegment, index) => {
                return [...children, index > 0 && <br key={index} />, textSegment];
            }, []),
        renderNode: {
            [INLINES.HYPERLINK]: (node, children) => {
                const { uri } = node.data;
                return (
                    <a href={uri} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                );
            },
        },
    };

    return (
        <AnimatePresence mode="wait">
            <motion.div
                className="commission-project"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
            >

                <Header showSlider={isModalOpen} onCloseSlider={() => setIsModalOpen(false)} pageTitle={commission?.title || "Portraits"} isCommissionSlider={true} isCommissionModalOpen={isModalOpen} buttons={buttons} />
                <Helmet>
                    <title>{commission.title}</title>
                    <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <link rel="canonical" href={`https://alexdebrabant.com/${commission.slug}`} />
                    <meta property="og:title" content={`${commission.title}`} />
                    <meta property="og:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={`${commission.title}`} />
                    <meta name="twitter:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                </Helmet>

                {!allImagesReady && (
                    <div className="loading-container">
                        <p className="loading-text">loading</p>
                    </div>
                )}

                <div className="mySwiperContainer" style={{ opacity: allImagesReady ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}>
                    <Swiper
                        navigation={true}
                        modules={[Navigation, Keyboard, FreeMode, Mousewheel]}
                        loop={true}
                        freeMode={true}
                        className="mySwiper"
                        keyboard={true}
                        slidesPerView="auto"
                        spaceBetween={15}
                        touchRatio={0.7}
                        threshold={5}
                        longSwipesRatio={0.5}
                        preventInteractionOnTransition={true}
                        mousewheel={{
                            forceToAxis: true,
                            sensitivity: 1,
                            releaseOnEdges: true,
                            thresholdDelta: 40,
                            thresholdTime: 200
                        }}
                        lazy={{
                            loadPrevNext: true,
                            loadPrevNextAmount: 1,
                        }}
                    >
                        {allImages.map((image, index) => (
                            <SwiperSlide key={index}>
                                {isVideo(image) ? (
                                    <motion.video
                                        src={getVideoUrl(image)}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        onClick={() => handleImageClick(index)}
                                        onLoadedData={() => handleVideoLoad(index)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.25 }}
                                    />
                                ) : (
                                    <motion.img
                                        src={getImageUrl(image)}
                                        alt={commission.title}
                                        onLoad={(e) => handleImageLoad(e, index)}
                                        width={imageDimensions[index]?.width || 'auto'}
                                        height={imageDimensions[index]?.height || 'auto'}
                                        onClick={() => handleImageClick(index)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.25 }}
                                    />
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {isModalOpen && (
                    <AnimatePresence>
                        <motion.div
                            className={`modal-overlay open ${modalReady ? 'ready' : ''}`}
                            onClick={() => setIsModalOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {!modalImagesLoaded[activeIndex] && (
                                <div className="loading-container-modal">
                                    <p className="loading-text">loading</p>
                                </div>
                            )}
                            <div
                                className="modal-content"
                                onClick={e => e.stopPropagation()}
                            >

                                <Swiper
                                    modules={[Navigation, Keyboard, Mousewheel, Zoom]}
                                    navigation={!hasAnyVideo}
                                    keyboard={true}
                                    initialSlide={activeIndex}
                                    className="modal-swiper"
                                    loop={true}
                                    centeredSlides={true}
                                    onSwiper={setModalSwiper}
                                    zoom={{ toggle: false }}
                                    onInit={(swiper) => {
                                        const slides = swiper.slides || [];
                                        slides.forEach(slide => {
                                            const vid = slide.querySelector('video');
                                            if (!vid) return;
                                            if (slide.classList.contains('swiper-slide-active')) {
                                                vid.muted = false; // active starts unmuted
                                            } else {
                                                try { vid.pause(); } catch (_) { }
                                                vid.muted = true;
                                            }
                                        });
                                    }}

                                    onSlideChange={(swiper) => {
                                        setCurrentSlide(swiper.realIndex + 1);
                                        setActiveImage(allImages[swiper.realIndex]);
                                        if (!modalImagesLoaded[swiper.realIndex]) {
                                            setModalImagesLoaded(prev => ({ ...prev, [swiper.realIndex]: false }));
                                        }
                                    }}
                                    onSlideChangeTransitionEnd={(swiper) => {
                                        const slides = swiper.slides || [];
                                        const activeEl = slides[swiper.activeIndex];
                                        slides.forEach((slideEl) => {
                                            const vid = slideEl && slideEl.querySelector && slideEl.querySelector('video');
                                            if (!vid) return;
                                            if (slideEl === activeEl) {
                                                vid.muted = false; // unmute active after transition completes
                                            } else {
                                                try { vid.pause(); } catch (_) { }
                                                vid.muted = true;
                                            }
                                        });
                                        // refresh play handlers for clones
                                        const allVideos = (swiper.el && swiper.el.querySelectorAll) ? swiper.el.querySelectorAll('video') : [];
                                        allVideos.forEach((vid) => {
                                            vid.onplay = () => {
                                                allVideos.forEach(v => {
                                                    if (v !== vid) {
                                                        try { v.pause(); } catch (_) { }
                                                        v.muted = true;
                                                    }
                                                });
                                                vid.muted = false;
                                            };
                                        });
                                    }}



                                    touchRatio={0.7}
                                    threshold={5}
                                    longSwipesRatio={0.5}
                                    preventInteractionOnTransition={true}

                                    mousewheel={{
                                        enabled: true,
                                        // forceToAxis: true,
                                        sensitivity: 0.4,
                                        releaseOnEdges: true,
                                        thresholdDelta: 60,
                                        //thresholdTime: 250
                                    }}
                                    speed={600}
                                    lazy={{
                                        loadPrevNext: true,
                                        loadPrevNextAmount: 1,
                                    }}
                                >
                                    <div className="modal-footer">
                                        <div className="modal-footer-text">
                                            <p className="commission-title">{activeImage?.title || ''}</p>
                                            <p className="commission-second-line">{activeImage?.description || ''}</p>
                                        </div>
                                        <p className="slide-counter">{currentSlide} of {allImages.length}</p>
                                    </div>
                                    {allImages.map((image, index) => (

                                        <SwiperSlide key={index}>
                                            {isVideo(image) ? (

                                                <video
                                                    src={getVideoUrl(image)}
                                                    // autoPlay
                                                    // muted
                                                    controls
                                                    loop
                                                    playsInline
                                                    onLoadedData={() => handleModalVideoLoad(index)}
                                                />

                                            ) : (
                                                <div className="swiper-zoom-container">
                                                    <img
                                                        src={getFullScreenUrl(image)}
                                                        alt={commission.title}
                                                        onLoad={(e) => handleModalImageLoad(e, index)}
                                                        width={imageDimensions[index]?.width || 'auto'}
                                                        height={imageDimensions[index]?.height || 'auto'}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            )}
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                )}

                {(commission.textTitle || commission.texte) && (
                    <div className="commission-text">
                        <p className="commission-text-title">{commission.textTitle}</p>
                        <div className="commission-text-body">{commission.texte && documentToReactComponents(commission.texte, renderOptions)}</div>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
}

export default Portraits02Slider;